/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-06
 */

// Add third-party dependencies.
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

// Add local dependencies.
import { useSessionAuth } from '../../hooks/useSessionAuth';

// Define component props interface.
interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
  _requiredRole?: string; // Future enhancement for role-based access
}

/**
 * Protected Route Component
 *
 * Wraps components that require authentication. Automatically redirects
 * unauthenticated users to the login page while preserving their intended
 * destination for post-login redirect.
 *
 * Features:
 * - Session-based authentication check with initialization awareness
 * - Smooth SPA navigation (no page reloads)
 * - Loading state during auth verification
 * - Preserves intended destination
 * - Future-ready for role-based access control
 * - Robust handling of session initialization states
 *
 * @param children - The component(s) to render if authenticated
 * @param redirectTo - Path to redirect to if not authenticated (default: '/login')
 * @param requiredRole - Optional role requirement for future enhancement
 * @returns Protected component or redirect
 *
 * @example
 * <ProtectedRoute>
 *   <UserDashboard />
 * </ProtectedRoute>
 *
 * @example
 * <ProtectedRoute redirectTo="/signin" requiredRole="admin">
 *   <AdminPanel />
 * </ProtectedRoute>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/login',
  _requiredRole,
}) => {
  const { isAuthenticated, isLoading, isInitialized } = useSessionAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication or initializing
  if (isLoading || !isInitialized) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          {!isInitialized
            ? 'Initializing session...'
            : 'Verifying authentication...'}
        </Typography>
      </Box>
    );
  }

  // Only redirect if session is fully initialized and user is not authenticated
  if (isInitialized && !isAuthenticated) {
    // Preserve the intended destination for post-login redirect
    const from = location.pathname + location.search;
    return <Navigate to={redirectTo} state={{ from }} replace />;
  }

  // TODO: Future enhancement - Role-based access control
  // if (_requiredRole && !userHasRole(_requiredRole)) {
  //   return <Navigate to="/unauthorized" replace />;
  // }

  // User is authenticated, render the protected content
  return children as React.ReactElement;
};

export default ProtectedRoute;
